/** @file Cascading removal of an entity (bone: slots, attachments, constraints, sockets, timelines). */
import type { Doc } from './schema';
import { ENTITY_KINDS, type EntityKind } from './entityKinds';
import { collectRemovals } from './collectRemovals';
import { throwError } from '../throwError';
import { stripAnimationRefs } from '../_internal/stripAnimationRefs';
import { stripSkeletonRefs } from '../_internal/stripSkeletonRefs';

/** Moves slots that survive a socket removal off its removed bones onto its parent bone (the item is kept). */
function rehomeSocketSlots(draft: Doc, socketId: string, removed: ReadonlyMap<string, EntityKind>): void {
  const socket = draft.sockets[socketId];
  if (!socket) return;
  for (const [slotId, slot] of Object.entries(draft.slots)) if (!removed.has(slotId) && removed.has(slot.bone)) slot.bone = socket.parentBone;
}

/** Removes `id` of `kind` from a mutable document draft and every reference to it. Returns the removed ids. */
export function removeEntity(draft: Doc, kind: EntityKind, id: string): string[] {
  const table = ENTITY_KINDS[kind];
  if (!Object.hasOwn(draft[table], id)) throwError('NOT_FOUND', { kind, ref: id }, { path: `${table}.${id}` });
  if (kind === 'skin' && draft.defaultSkin === id) {
    throwError('VALIDATION', {}, { path: 'defaultSkin', hint: 'the default skin cannot be removed; make another skin the default first' });
  }
  const removed = collectRemovals(draft, kind, id);
  if (kind === 'socket') rehomeSocketSlots(draft, id, removed);
  stripAnimationRefs(draft, removed);
  stripSkeletonRefs(draft, removed);
  for (const [gone, goneKind] of removed) Reflect.deleteProperty(draft[ENTITY_KINDS[goneKind]], gone);
  return [...removed.keys()];
}
